import { useState, useEffect } from 'react'
import { potencySuffix } from '@/lib/potency'
import { X, Package, AlertTriangle, Check, Loader2 } from 'lucide-react'
import { useUIStore } from '@/stores/uiStore'
import { usePOSStore } from '@/stores/posStore'
import { useProductBatches } from '@/api/products'
import type { Product, ProductLot } from '@/types'

export function LotSelectModal() {
  const { closeModal, modalData } = useUIStore()
  const { addToCart, changeBatch } = usePOSStore()
  const [selectedLot, setSelectedLot] = useState<ProductLot | null>(null)
  const [quantity, setQuantity] = useState(1)

  const product = modalData?.product as Product | undefined
  const isChangingBatch = modalData?.isChangingBatch as boolean | undefined
  const cartItemId = modalData?.cartItemId as string | undefined
  
  // Fetch batches from API
  const { data: batches, isLoading } = useProductBatches(product?.id ?? null, !!product)
  
  const lots = batches || []

  useEffect(() => {
    // Auto-select first available lot or current lot
    if (lots.length > 0 && !selectedLot) {
      // If changing batch, try to select current batch
      if (isChangingBatch && product?.lot_number) {
        const currentLot = lots.find(lot => lot.lot_number === product.lot_number)
        if (currentLot) {
          setSelectedLot(currentLot)
          return
        }
      }
      // Otherwise select first available
      const firstAvailable = lots.find(lot => lot.quantity > 0)
      if (firstAvailable) {
        setSelectedLot(firstAvailable)
      }
    }
  }, [lots, selectedLot, isChangingBatch, product])

  if (!product) return null

  const handleAddToCart = () => {
    if (lots.length > 0 && !selectedLot) {
      return // Must select a lot for cannabis products
    }
    
    if (isChangingBatch && cartItemId) {
      // Change batch for existing cart item
      changeBatch(cartItemId, selectedLot?.lot_number ?? '')
    } else {
      // Add new item to cart with selected batch
      addToCart(product, quantity, selectedLot?.lot_number ?? null)
    }
    closeModal()
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A'
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const isExpiringSoon = (dateStr: string | null) => {
    if (!dateStr) return false
    const expiry = new Date(dateStr)
    const today = new Date()
    const daysUntilExpiry = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    return daysUntilExpiry <= 30
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={closeModal}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Package size={20} className="text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {isChangingBatch ? 'Change Batch/Lot' : 'Select Batch/Lot'}
              </h2>
              <p className="text-sm text-gray-500">{product.name || product.description}</p>
            </div>
          </div>
          <button
            onClick={closeModal}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading ? (
            <div className="text-center py-8">
              <Loader2 size={48} className="mx-auto mb-4 animate-spin text-green-600" />
              <p className="text-gray-500">Loading available batches...</p>
            </div>
          ) : lots.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Package size={48} className="mx-auto mb-4 opacity-50" />
              <p>No batches available for this product.</p>
              <p className="text-sm mt-2">Add product without batch tracking.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-4">
                Select a batch for compliance tracking:
              </p>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {lots.map((lot) => (
                  <button
                    key={lot.lot_number}
                    onClick={() => setSelectedLot(lot)}
                    disabled={lot.quantity <= 0}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      selectedLot?.lot_number === lot.lot_number
                        ? 'border-green-500 bg-green-50 cursor-pointer'
                        : lot.quantity <= 0
                        ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                        : 'border-gray-200 hover:border-green-300 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">
                        Lot #{lot.lot_number}
                      </span>
                      {selectedLot?.lot_number === lot.lot_number && (
                        <Check size={20} className="text-green-600" />
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-gray-500">
                        Qty: {lot.quantity} available
                      </span>
                      <span className={`flex items-center gap-1 justify-end ${
                        isExpiringSoon(lot.expiration_date)
                          ? 'text-amber-600'
                          : 'text-gray-500'
                      }`}>
                        {isExpiringSoon(lot.expiration_date) && (
                          <AlertTriangle size={14} />
                        )}
                        Exp: {formatDate(lot.expiration_date)}
                      </span>
                      {lot.thc !== null && lot.thc !== undefined && (
                        <span className="text-gray-500">
                          THC: {lot.thc}{potencySuffix(lot.test_units)}
                        </span>
                      )}
                      {lot.cbd !== null && lot.cbd !== undefined && lot.cbd > 0 && (
                        <span className="text-gray-500">
                          CBD: {lot.cbd}{potencySuffix(lot.test_units)}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Quantity Selector - Only show when adding new item */}
          {!isChangingBatch && (
            <div className="mt-6 flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700">Quantity:</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center font-semibold transition-colors cursor-pointer"
                >
                  -
                </button>
                <span className="w-12 text-center font-semibold text-lg">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  disabled={!!(selectedLot && quantity >= selectedLot.quantity)}
                  className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-semibold transition-colors cursor-pointer"
                >
                  +
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 flex gap-3">
            <button
              onClick={closeModal}
              className="flex-1 py-3 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleAddToCart}
              disabled={(lots.length > 0 && !selectedLot) || isLoading}
              className="flex-1 py-3 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors cursor-pointer"
            >
              {isChangingBatch ? 'Change Batch' : 'Add to Cart'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
